import type { FC, ReactNode } from 'react'
import type { WashPackage } from '@/types'

import Link from 'next/link'
import { Check, Clock } from 'lucide-react'
import { cn } from '@/lib/utils'
import { buttonStyles } from '@/lib/button-styles'

interface PackageCardProps {
    pkg: WashPackage
}

export const PackageCard: FC<PackageCardProps> = ({ pkg }): ReactNode => {
    const featured = pkg.featured

    return (
        <article
            className={cn(
                'relative flex flex-col rounded-[1.75rem] p-7 transition-shadow duration-300',
                featured
                    ? 'bg-ink text-white shadow-2xl shadow-ink/25 xl:-translate-y-4'
                    : 'border border-border bg-card shadow-sm hover:shadow-xl hover:shadow-ink/5'
            )}
        >
            {pkg.badge && (
                <span
                    className='absolute -top-3.5 left-7 rounded-full bg-primary px-3.5 py-1 text-xs font-bold uppercase tracking-[0.14em] text-primary-foreground shadow-sm'
                    data-mdlfy-cms={`packages:${pkg.id}:badge`}
                >
                    {pkg.badge}
                </span>
            )}

            <h3
                className={cn('font-heading text-[1.9rem] font-bold uppercase leading-none tracking-wide', featured ? 'text-white' : 'text-ink')}
                data-mdlfy-cms={`packages:${pkg.id}:name`}
            >
                {pkg.name}
            </h3>

            {pkg.tagline && (
                <p
                    className={cn('mt-3 text-[0.95rem] leading-relaxed', featured ? 'text-white/70' : 'text-muted-foreground')}
                    data-mdlfy-cms={`packages:${pkg.id}:tagline`}
                >
                    {pkg.tagline}
                </p>
            )}

            {(pkg.price || pkg.duration) && (
                <div className={cn('mt-6 flex items-end justify-between gap-3 border-y py-5', featured ? 'border-white/10' : 'border-border')}>
                    {pkg.price && (
                        <p className='font-heading text-[3.25rem] font-bold leading-none'>
                            <span data-mdlfy-cms={`packages:${pkg.id}:price`}>{pkg.price}</span>
                        </p>
                    )}

                    {pkg.duration && (
                        <p
                            className={cn(
                                'mb-1 inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-sm font-semibold',
                                featured ? 'bg-white/10 text-white' : 'bg-foam text-aqua-deep'
                            )}
                        >
                            <Clock className='size-4' />
                            <span data-mdlfy-cms={`packages:${pkg.id}:duration`}>{pkg.duration}</span>
                        </p>
                    )}
                </div>
            )}

            {pkg.features.length > 0 ? (
                <ul className='mt-6 flex-1 space-y-3 text-[0.95rem]'>
                    {pkg.features.map((feature, index) => (
                        <li key={`${index}-${feature}`} className='flex gap-3'>
                            <span
                                className={cn(
                                    'mt-0.5 grid size-5 shrink-0 place-items-center rounded-full',
                                    featured ? 'bg-aqua text-ink' : 'bg-foam text-aqua-deep'
                                )}
                            >
                                <Check className='size-3.5' strokeWidth={3} />
                            </span>

                            <span className={featured ? 'text-white/85' : 'text-ink/85'}>
                                {feature}
                            </span>
                        </li>
                    ))}
                </ul>
            ) : (
                <div className='flex-1' />
            )}

            <Link
                href={`/book?package=${encodeURIComponent(pkg.id)}`}
                aria-label={`Book the ${pkg.name}`}
                className={buttonStyles({ variant: featured ? 'primary' : 'dark', className: 'mt-8 w-full' })}
            >
                Book this wash
            </Link>
        </article>
    )
}
